'use strict';

import * as util from '../util/Common.js';
import {Point2D} from '../util/Geometry.js';
import * as domUtil from '../util/Dom.js';
import {ThreeContentControl} from '../content/ThreeContent.js';

const privateMap = util.createPrivateMapper();

const handleMousewheelZoom = function(view, threeContentControl, zoomFactor, ev){
  ev.preventDefault();
  const delta = ev.wheelDelta ? ev.wheelDelta : -ev.detail;
  if (delta < 0) {
    threeContentControl.zoom += zoomFactor;
  } else {
    threeContentControl.zoom -= zoomFactor;
  }
  view.requestRender();
};

const mouse = new THREE.Vector2();
const point1 = new THREE.Vector2();
const point2 = new THREE.Vector2();

const setThreePoint = function(view, point, vector2) {
  const sz = view.size;
  const px = domUtil.getPixel(view.domEl, point);
  vector2.x = (px.x / sz.x) * 2 - 1;
  vector2.y = - (px.y / sz.y) * 2 + 1;
}
const updateMouse = function(view, touch){
  setThreePoint(view, touch.center, mouse);
};

class NormalDrag {
  constructor(){
  }
  start(view, threeContentControl, ev, touch) {
    ev.preventDefault();
    updateMouse(view, touch);
    this.prev = touch.center;
    return true;
  }

  drag(view, threeContentControl, ev, touch) {
    updateMouse(view, touch);

    const pt = touch.center;
    const dpt = pt.sub(this.prev);
    this.prev = pt;
    const scale = 1 / 360; // (10 / Math.min(sz.x, sz.y)) * (dist.length() / camera.far);
    const xdelta = Math.PI * (-dpt.y * scale);
    const ydelta = Math.PI * (-dpt.x * scale);
    threeContentControl.rotateX(xdelta);
    threeContentControl.rotateY(ydelta);
    view.requestRender();
  }

  end(view, threeContentControl, ev, touch) {

  }
}
class PanDrag {
  constructor(){
  }
  start(view, threeContentControl, ev, touch) {
    ev.preventDefault();
    this.prev = touch.center;
    return true;
  }

  drag(view, threeContentControl, ev, touch) {
    const pt = touch.center;
    setThreePoint(view, this.prev, point1);
    setThreePoint(view, pt, point2);
    threeContentControl.pan(point1, point2);
    view.requestRender();
    this.prev = pt;
  }

  end(view, threeContentControl, ev) {

  }
}
class RaycastDrag {
  constructor(){
  }
  start(view, threeContentControl, ev, touch) {
    ev.preventDefault();
    const pt = touch.center;
    setThreePoint(view, pt, point1);
    threeContentControl.startRaycastDrag(point1);
    view.requestRender();
    return true;
  }

  drag(view, threeContentControl, ev, touch) {
    const pt = touch.center;
    setThreePoint(view, pt, point1);
    threeContentControl.moveRaycastDrag(point1);
    view.requestRender();
  }

  end(view, threeContentControl, ev, touch) {
    threeContentControl.endRaycastDrag();
    view.requestRender();
  }
}
class ZAxisRotationDrag {
  constructor(){
  }
  start(view, threeContentControl, ev, touch) {
    ev.preventDefault();
    const pt = touch.center;
    this.prev = pt;
    return true;
  }

  drag(view, threeContentControl, ev, touch) {
    const pt = touch.center;
    const prevPx = domUtil.getPixel(view.domEl, this.prev);
    const px = domUtil.getPixel(view.domEl, pt);
    const dpt = pt.sub(view.center);
    this.prev = pt;
    if (prevPx.equals(view.center) || px.equals(view.center)) {
      // No angle
      return;
    }
    const angle = prevPx.sub(view.center).angle() - px.sub(view.center).angle();
    threeContentControl.rotateZ(angle);
    view.requestRender();
  }

  end(view, threeContentControl, ev, touch) {
  }
}

const DRAG_MODES = {
  normal: new NormalDrag(),
  pan: new PanDrag(),
  raycast: new RaycastDrag(),
  zaxis: new ZAxisRotationDrag()
}

export class ThreeViewControl {
  constructor(argOptions){
    const pv = privateMap(this);
    const options = argOptions ? argOptions : {};
    pv.options = options;
    pv.dragModeComponents = {};
    this.mouseWheelDisabled = false;
    pv.zoomFactor = 0.05;
    pv.mousewheelHandler = (ev) => {
      if (this.mouseWheelDisabled) {
        return;
      }
      handleMousewheelZoom(pv.view, pv.threeContentControl, pv.zoomFactor, ev);
    }
    pv.drag = DRAG_MODES[pv.dragMode];
    pv.touching = false;
    pv.dragHandler = (ev) => {
      if (this.dragMoveDisabled) {
        return;
      }
      if (pv.drag.start(pv.view, pv.threeContentControl, ev, domUtil.generateTouch(ev)) === false) {
        return;
      }
      const moveHandler = (ev) => {
        pv.drag.drag(pv.view, pv.threeContentControl, ev, domUtil.generateTouch(ev));
      };
      domUtil.addEventListener(window, 'mousemove', moveHandler);

      const endHandler = (ev) => {
        domUtil.removeEventListener(window, 'mousemove', moveHandler);
        domUtil.removeEventListener(window, 'mouseup', endHandler);
        pv.drag.end(pv.view, pv.threeContentControl, ev, domUtil.generateTouch(ev));
      };
      domUtil.addEventListener(window, 'mouseup', endHandler);
    };

    pv.touchHandler = (ev) => {
      if (this.dragMoveDisabled) {
        return;
      }
      const touch = domUtil.getTouch(ev);
      let prevTouch = touch;
      if (!pv.touching && touch.points.length == 1) {
        if (pv.drag.start(pv.view, pv.threeContentControl, ev, touch) === false) {
          return;
        }
      }
      pv.touching = true;
      const moveHandler = (ev) => {
        const touch = domUtil.getTouch(ev);
        const pTouch = prevTouch;
        prevTouch = touch;
        if (!pTouch) {
          return;
        }
        if (pTouch.points.length == 1 && touch.points.length == 1) {
          // Drag
          pv.drag.drag(pv.view, pv.threeContentControl, ev, touch);
        } else if (pTouch.points.length > 1) {
          // Zoom
          pv.threeContentControl.zoom += (pTouch.distance - touch.distance) * pv.zoomFactor / 20;
          pv.view.requestRender();
        }
      };
      domUtil.addEventListener(window, 'touchmove', moveHandler);

      const endHandler = (ev) => {
        const touch = domUtil.getTouch(ev);
        prevTouch = touch;
        if (touch.points.length > 0) {
          if (touch.points.length == 1) {
            if (pv.drag.start(pv.view, pv.threeContentControl, ev, touch) === false) {
            }
          }
          return;
        }
        pv.touching = false;
        domUtil.removeEventListener(window, 'touchmove', moveHandler);
        domUtil.removeEventListener(window, 'touchend', endHandler);
        pv.drag.end(pv.view, pv.threeContentControl, createTouchMouseEvent(ev));
      };
      domUtil.addEventListener(window, 'touchend', endHandler);
    };
    pv.contentChangeHandler = (ev) => {
      const pv = privateMap(this);
      domUtil.removeEventListener(pv.view.eventLayer, 'mousewheel', pv.mousewheelHandler);
      domUtil.removeEventListener(pv.view.eventLayer, 'mousedown', pv.dragHandler);
      domUtil.removeEventListener(pv.view.eventLayer, 'touchstart', pv.touchHandler);
      
      pv.threeContentControl = null;
      if (pv.view && pv.view.resource) {
        domUtil.addEventListener(pv.view.eventLayer, 'mousewheel', pv.mousewheelHandler);
        domUtil.addEventListener(pv.view.eventLayer, 'mousedown', pv.dragHandler);
        domUtil.addEventListener(pv.view.eventLayer, 'touchstart', pv.touchHandler);
        pv.threeContentControl = pv.view.control.three;
        if (pv.threeContentControl) {
          if (options.ambientLightColor) {
            pv.threeContentControl.disposition.ambientLight.color.set(options.ambientLightColor);
          }
          if (options.ambientLightIntensity) {
            pv.threeContentControl.disposition.ambientLight.intensity = options.ambientLightIntensity;
          }
          if (options.cameraLightColor) {
            pv.threeContentControl.disposition.cameraLight.color.set(options.cameraLightColor);
          }
          if (options.cameraLightIntensity) {
            pv.threeContentControl.disposition.cameraLight.intensity = options.cameraLightIntensity;
          }
        }
        pv.threeContentControl.animation = null;
        // pv.threeContentControl.animation = {
        //   lastTime: (new Date()).getTime(),
        //   process: function(now){
        //     if (!pv.threeContentControl) {
        //       return false;
        //     }
        //     const t = now - this.lastTime;
        //     pv.threeContentControl.rotateY(t * Math.PI / 60000);
        //     this.lastTime = now;
        //     return true;
        //   }
        // }
        if (options.components) {
          util.each(options.components, (component) => {
            component.link(pv.view);
          });
        }
      }
    }
    if (options.components) {
      util.each(options.components, (component) => {
        const clickAction = component.domEl.getAttribute('data-click-action');
        if (clickAction) {
          domUtil.addEventListener(component.domEl, 'click', (ev) => {
            ev.preventDefault();
            if (!this[clickAction]) {
              return;
            }
            this[clickAction]();
          })
        }
        const dragMode = component.domEl.getAttribute('data-click-drag-mode');
        if (dragMode) {
          let dComponents = pv.dragModeComponents[dragMode];
          if (!dComponents) {
            dComponents = [];
            pv.dragModeComponents[dragMode] = dComponents;
          }
          dComponents.push(component);
          domUtil.addEventListener(component.domEl, 'click', (ev) => {
            ev.preventDefault();
            this.dragMode = dragMode;
          })
        }
      });
    }
    this.unlink();
  }

  link(view) {
    const pv = privateMap(this);
    if (pv.view) {
      this.unlink();
    }
    if (!view) {
      return;
    }
    pv.view = view;
    domUtil.addEventListener(pv.view.eventLayer, 'ui.contentchange', pv.contentChangeHandler);
    this.viewSize = view.size;
    pv.requestRender = function(){
      view.requestRender();
    };
  }

  unlink(){
    const pv = privateMap(this);
    if (pv.view) {
      domUtil.removeEventListener(pv.view.eventLayer, 'mousewheel', pv.mousewheelHandler);
      domUtil.removeEventListener(pv.view.eventLayer, 'mousedown', pv.dragHandler);
      domUtil.removeEventListener(pv.view.eventLayer, 'ui.contentchange', pv.contentChangeHandler);
    }
    if (pv.options.components) {
      util.each(pv.options.components, (component) => {
        component.unlink();
      });
    }
    pv.requestRender = function(){};
    pv.view = null;
  }

  set dragMode(mode){
    const pv = privateMap(this);
    if (pv.dragMode) {
      util.each(pv.dragModeComponents[pv.dragMode], function(component){
        domUtil.dispatchEvent(component.domEl, 'ui.three.dragMode', {
          detail: false
        });
      });
    }
    const d = DRAG_MODES[mode];
    if (!d) {
      return;
    }
    pv.dragMode = mode;
    pv.drag = d;
    util.each(pv.dragModeComponents[pv.dragMode], function(component){
      domUtil.dispatchEvent(component.domEl, 'ui.three.dragMode', {
        detail: true
      });
    });
  }
  get dragMode(){
    return privateMap(this).dragMode;
  }
  get dragModes(){
    const ret = [];
    for (k in DRAG_MODES) {
      ret.push(k);
    }
    return ret;
  }
  // get disposition(){
  //   return privateMap(this).threeContentControl.disposition;
  // }
  // set resource(resource){
  //   privateMap(this).threeContentControl.resource = resource;
  // }
  set viewSize(sz){
    const pv = privateMap(this);
    if (!pv.view) {
      return;
    }
    // pv.threeContentControl.viewSize = sz;
  }

  homeView(){
    const pv = privateMap(this);
    if (!pv.threeContentControl || !pv.view) {
      return;
    }
    pv.threeContentControl.homeView();
    pv.view.requestRender();
  }

  startAnimation(){
    const pv = privateMap(this);
    if (!pv.threeContentControl || !pv.view) {
      return;
    }
    pv.threeContentControl.animation = {
      lastTime: (new Date()).getTime(),
      process: function(now){
        if (!pv.threeContentControl) {
          return false;
        }
        const t = now - this.lastTime;
        pv.threeContentControl.rotateY(t * Math.PI / 60000);
        // pv.threeContentControl.objectRotateY(t * Math.PI / 60000);
        this.lastTime = now;
        return true;
      }
    }
    domUtil.dispatchEvent(pv.view.domEl, 'ui.three.startAnimation');
    pv.view.requestRender();
  }

  stopAnimation(){
    const pv = privateMap(this);
    if (!pv.threeContentControl || !pv.view) {
      return;
    }
    pv.threeContentControl.animation = null;
    domUtil.dispatchEvent(pv.view.domEl, 'ui.three.stopAnimation');
  }

  toggleAnimation(){
    const pv = privateMap(this);
    if (!pv.threeContentControl || !pv.view) {
      return;
    }
    if (pv.threeContentControl.animation) {
      this.stopAnimation();
    } else {
      this.startAnimation();
    }
  }
}